import { APIError } from '../api/studysnap';
import { mcqService } from './mcq';
import { FlashcardSet } from '../types/flashcard';
import { TranscriptSegment } from '../types/transcript';
import { BookData } from '../types/book';
import { MCQData } from '../types/mcq';
import { GlossaryData } from '../types/glossary';

interface VideoDetails {
    id: string;
    title: string;
    description: string;
    channelTitle: string;
    publishedAt: string;
    viewCount: string;
    likeCount: string;
    duration: string;
    thumbnail: string;
    categoryId: string;
}

interface TranscriptSegment {
    text: string;
    duration: number;
    offset: number;
}

export interface ProcessedVideoData {
    videoDetails: VideoDetails;
    transcript: string;
    rawTranscript: TranscriptSegment[];
    mcq?: {
        questions: any[];
        html: string;
    };
    glossary?: {
        content: string;
    };
    book?: {
        title: string;
        chapters: Array<{
            title: string;
            content: string;
        }>;
        videoInfo: any;
    };
    flashcards?: FlashcardSet;
}

interface ProcessVideoOptions {
    generateMCQ?: boolean;
    generateGlossary?: boolean;
    generateBook?: boolean;
    generateFlashcards?: boolean;
}

// Remove the hardcoded mapping
interface CategoryInfo {
    id: string;
    title: string;
}

export class YouTubeService {
    private static readonly youtubeApiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
    private static readonly rapidApiKey = import.meta.env.VITE_RAPIDAPI_KEY;
    private static categoryCache: Map<string, string> = new Map();
    private static lastCategoryFetch: number = 0;
    private static readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    static {
        if (!this.youtubeApiKey) {
            console.error('YouTube API key is not configured');
        }
        if (!this.rapidApiKey) {
            console.error('RapidAPI key is not configured');
        }
    }

    /**
     * Extracts video ID from a YouTube URL or returns the ID if already in ID format
     */
    static extractVideoId(urlOrId: string): string {
        // If it's already an ID (no slashes or spaces)
        if (!/[\/\s]/.test(urlOrId) && urlOrId.length === 11) {
            return urlOrId;
        }

        try {
            const url = new URL(urlOrId);
            let videoId = '';

            if (url.hostname === 'youtu.be') {
                videoId = url.pathname.slice(1);
            } else if (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com') {
                if (url.pathname === '/watch') {
                    videoId = url.searchParams.get('v') || '';
                } else if (url.pathname.startsWith('/embed/')) {
                    videoId = url.pathname.split('/')[2];
                } else if (url.pathname.startsWith('/v/')) {
                    videoId = url.pathname.split('/')[2];
                }
            }

            if (!videoId) {
                throw new Error('Could not extract video ID from URL');
            }

            return videoId;
        } catch (error) {
            throw new Error('Invalid YouTube URL or video ID');
        }
    }

    /**
     * Fetches video details using YouTube Data API
     */
    static async getVideoDetails(videoId: string): Promise<VideoDetails> {
        if (!this.youtubeApiKey) {
            throw new Error('YouTube API key is not configured');
        }

        const url = `https://www.googleapis.com/youtube/v3/videos?part=snippet,statistics,contentDetails&id=${videoId}&key=${this.youtubeApiKey}`;
        
        const response = await fetch(url);
        const data = await response.json();

        if (!response.ok) {
            throw new Error(`YouTube API Error: ${response.status} - ${JSON.stringify(data)}`);
        }

        if (!data.items || data.items.length === 0) {
            throw new Error('Video not found');
        }

        const video = data.items[0];
        return {
            id: video.id,
            title: video.snippet.title,
            description: video.snippet.description,
            channelTitle: video.snippet.channelTitle,
            publishedAt: video.snippet.publishedAt,
            viewCount: video.statistics.viewCount,
            likeCount: video.statistics.likeCount,
            duration: video.contentDetails.duration,
            thumbnail: video.snippet.thumbnails.maxres?.url || 
                      video.snippet.thumbnails.high?.url ||
                      video.snippet.thumbnails.medium?.url ||
                      video.snippet.thumbnails.default?.url,
            categoryId: video.snippet.categoryId
        };
    }

    /**
     * Fetches video transcript using RapidAPI
     */
    static async getTranscript(videoId: string): Promise<TranscriptSegment[]> {
        if (!this.rapidApiKey) {
            throw new Error('RapidAPI key is not configured');
        }

        const url = `https://youtube-transcript3.p.rapidapi.com/api/transcript-with-url?url=${encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`)}&flat_text=true&lang=en`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'X-RapidAPI-Key': this.rapidApiKey,
                'X-RapidAPI-Host': 'youtube-transcript3.p.rapidapi.com'
            }
        });

        if (!response.ok) {
            throw new Error(`Transcript API Error: ${response.status}`);
        }

        // Get the raw response text first
        const responseText = await response.text();
        console.log('Raw transcript response:', responseText.substring(0, 200));

        try {
            const transcriptData = JSON.parse(responseText);
            
            // Handle different response formats
            if (Array.isArray(transcriptData)) {
                // Format 1: Array of segments
                return transcriptData.map(segment => ({
                    text: segment.text,
                    duration: segment.duration,
                    offset: segment.offset
                }));
            } else if (typeof transcriptData === 'object' && transcriptData !== null) {
                // Format 2: Object with transcript property
                if (typeof transcriptData.transcript === 'string') {
                    // Single string transcript
                    return [{
                        text: transcriptData.transcript,
                        duration: 0,
                        offset: 0
                    }];
                } else if (Array.isArray(transcriptData.transcript)) {
                    // Array of transcript segments
                    return transcriptData.transcript.map((text: string) => ({
                        text,
                        duration: 0,
                        offset: 0
                    }));
                }
            }

            throw new Error('Unexpected transcript format');
        } catch (error) {
            console.error('Failed to parse transcript:', error);
            throw new Error('Failed to parse transcript response');
        }
    }

    /**
     * Formats duration string from YouTube API format to readable format
     */
    static formatDuration(duration: string): string {
        const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
        if (!match) return duration;

        const hours = match[1] ? match[1].replace('H', '') : '0';
        const minutes = match[2] ? match[2].replace('M', '') : '0';
        const seconds = match[3] ? match[3].replace('S', '') : '0';

        const parts = [];
        if (parseInt(hours) > 0) parts.push(`${hours}h`);
        if (parseInt(minutes) > 0) parts.push(`${minutes}m`);
        if (parseInt(seconds) > 0) parts.push(`${seconds}s`);

        return parts.join(' ');
    }

    private static formatTranscript(segments: TranscriptSegment[]): string {
        return segments.map(segment => segment.text).join(' ');
    }

    public static async generateBook(transcript: TranscriptSegment[], videoId: string, title: string, metadata?: { title: string; duration: number; author: string }): Promise<BookData> {
        const transcriptText = this.formatTranscript(transcript);
        const apiUrl = import.meta.env.VITE_API_URL;
        
        const response = await fetch(`${apiUrl}/api/generate-book`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                transcript: transcriptText,
                videoId,
                videoTopic: title,
                videoInfo: metadata
            }),
        });

        if (!response.ok) {
            throw new Error('Failed to generate book content');
        }

        return response.json();
    }

    public static async generateGlossary(transcript: TranscriptSegment[], videoId: string, title: string): Promise<GlossaryData> {
        try {
            console.log('Starting glossary generation in YouTubeService...');
            const transcriptText = this.formatTranscript(transcript);
            const apiUrl = import.meta.env.VITE_API_URL;
            
            console.log('Making request to glossary API:', `${apiUrl}/api/generate-glossary`);
            const response = await fetch(`${apiUrl}/api/generate-glossary`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    transcript: transcriptText,
                    videoId,
                    videoTopic: title
                }),
            });

            if (!response.ok) {
                console.error('Glossary API error:', response.status, response.statusText);
                throw new Error('Failed to generate glossary');
            }

            const data = await response.json();
            console.log('Received glossary data:', data);
            
            // Transform the data into the expected format
            return {
                terms: data.content.map((item: { term: string; definition: string }) => ({
                    term: item.term,
                    definition: item.definition
                }))
            };
        } catch (error) {
            console.error('Error in generateGlossary:', error);
            throw error;
        }
    }

    public static async generateMCQ(chapters: any[]): Promise<MCQData> {
        const apiUrl = import.meta.env.VITE_API_URL;
        
        const response = await fetch(`${apiUrl}/api/generate-chapter-mcq`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(chapters),
        });

        if (!response.ok) {
            throw new Error('Failed to generate MCQ content');
        }

        const data = await response.json();
        
        // Transform the chapter MCQs into the expected format
        const questions = data.flatMap((chapter: any) => 
            chapter.mcqs.map((mcq: any) => ({
                question: mcq.question,
                options: mcq.options,
                correctAnswer: mcq.correctAnswer,
                explanation: mcq.explanation,
                relatedConcept: mcq.relatedConcept
            }))
        );

        return {
            questions,
            html: '' // We don't need HTML for the new format
        };
    }

    public static async generateFlashcards(transcript: TranscriptSegment[], videoId: string, title: string): Promise<FlashcardSet | null> {
        try {
            console.log('Starting flashcard generation...');
            const transcriptText = this.formatTranscript(transcript);
            const apiUrl = import.meta.env.VITE_API_URL;
            
            console.log('Making request to flashcard API:', `${apiUrl}/api/generate-flashcards`);
            const response = await fetch(`${apiUrl}/api/generate-flashcards`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    transcript: transcriptText,
                    videoId,
                    videoTitle: title,
                }),
            });

            if (!response.ok) {
                console.error('Flashcard API error:', response.status, response.statusText);
                throw new Error('Failed to generate flashcards');
            }

            const data = await response.json();
            console.log('Received flashcard data:', data);
            
            const flashcardSet = {
                title: data.title || title,
                cards: data.cards.map((card: any, index: number) => ({
                    id: card.id || `card-${index + 1}`,
                    front: card.front,
                    back: card.back,
                    context: card.context
                })),
                totalCards: data.cards.length
            };
            console.log('Processed flashcard set:', flashcardSet);
            return flashcardSet;
        } catch (error) {
            console.error('Error generating flashcards:', error);
            return null;
        }
    }

    public static async generateMindmap(transcript: TranscriptSegment[], videoId: string, title: string): Promise<string> {
        try {
            console.log('Starting mindmap generation...');
            const transcriptText = this.formatTranscript(transcript);
            const apiUrl = import.meta.env.VITE_API_URL;
            
            console.log('Making request to mindmap API:', `${apiUrl}/api/generate-mindmap`);
            const response = await fetch(`${apiUrl}/api/generate-mindmap`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    transcript: transcriptText,
                    videoId,
                    videoTitle: title,
                }),
            });

            if (!response.ok) {
                console.error('Mindmap API error:', response.status, response.statusText);
                throw new Error('Failed to generate mindmap');
            }

            const data = await response.text();
            console.log('Received mindmap data:', data.substring(0, 200) + '...');
            return data;
        } catch (error) {
            console.error('Error generating mindmap:', error);
            return '';
        }
    }

    /**
     * Fetches the list of video categories from YouTube API
     */
    private static async fetchCategories(): Promise<void> {
        if (!this.youtubeApiKey) {
            throw new Error('YouTube API key is not configured');
        }

        try {
            const url = `https://www.googleapis.com/youtube/v3/videoCategories?part=snippet&regionCode=US&key=${this.youtubeApiKey}`;
            const response = await fetch(url);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(`YouTube API Error: ${response.status} - ${JSON.stringify(data)}`);
            }

            // Clear existing cache
            this.categoryCache.clear();

            // Update cache with new categories
            data.items.forEach((item: any) => {
                this.categoryCache.set(item.id, item.snippet.title);
            });

            this.lastCategoryFetch = Date.now();
        } catch (error) {
            console.error('Failed to fetch categories:', error);
            // If fetch fails, we'll fall back to existing cache or "Unknown Category"
        }
    }

    /**
     * Gets category name, fetching fresh data if cache is expired
     */
    static async getCategoryName(categoryId: string): Promise<string> {
        // Check if we need to refresh the cache
        if (Date.now() - this.lastCategoryFetch > this.CACHE_DURATION || this.categoryCache.size === 0) {
            await this.fetchCategories();
        }

        return this.categoryCache.get(categoryId) || 'Unknown Category';
    }

    static async processVideo(youtubeUrl: string, options: ProcessVideoOptions): Promise<ProcessedVideoData> {
        try {
            const videoId = this.extractVideoId(youtubeUrl);
            const videoDetails = await this.getVideoDetails(videoId);
            const rawTranscript = await this.getTranscript(videoId);
            const transcript = this.formatTranscript(rawTranscript);

            const result: ProcessedVideoData = {
                videoDetails,
                transcript,
                rawTranscript,
            };

            if (options.generateMCQ) {
                result.mcq = await this.generateMCQ(transcript, videoId, videoDetails.title);
            }

            if (options.generateGlossary) {
                result.glossary = await this.generateGlossary(transcript, videoId, videoDetails.title);
            }

            if (options.generateBook) {
                result.book = await this.generateBook(transcript, videoId, videoDetails.title, {
                    channelTitle: videoDetails.channelTitle,
                    publishedAt: videoDetails.publishedAt,
                    viewCount: videoDetails.viewCount,
                    likeCount: videoDetails.likeCount,
                    duration: this.formatDuration(videoDetails.duration),
                });
            }

            if (options.generateFlashcards) {
                result.flashcards = await this.generateFlashcards(transcript, videoId, videoDetails.title);
            }

            return result;
        } catch (error) {
            console.error('Error in processVideo:', error);
            throw error instanceof APIError ? error : new APIError('Failed to process video');
        }
    }
}

// Export a singleton instance
export const youtubeService = new YouTubeService(); 